import { debounce } from 'lodash';
import { Ref, ref, toRef } from 'vue';
import Booking from '@/booking/models/Booking';
import ContactContext from '@/contact/models/ContactContext';
import DossierContactService from '@/contact/services/DossierContactService';
import { DossierId } from '@/dossier/models/AbstractDossier';
import Dossier from '@/dossier/models/Dossier';
import LoadingService from '@/dossier/services/LoadingService';
import Identity from '@/general/models/Identity';
import pushService, { Unbinder } from '@/general/services/PushService';
import { Cache } from '@/general/utils/cache';
import ChatType, { visitChatType } from '@/shared/constants/ChatType';
import { OutletId } from '@/shared/models/Outlet';
import serviceEvents, { UserLoggedOut } from '@/shared/services/events/serviceEvents';
import { ifPresent, isPresent, present } from '@/shared/utils/typescript';
import ChatClient from '../client/ChatClient';
import ChatMessageClient from '../client/ChatMessageClient';
import ChatPage from '../constants/ChatPage';
import { parseChatMessage } from '../converters/chatMessageConverters';
import Chat from '../models/Chat';
import ChatContext from '../models/ChatContext';
import ChatMessage from '../models/ChatMessage';
import ChatTargetContainer from '../models/ChatTargetContainer';
import ExternalChatContext from '../models/ExternalChatContext';
import InternalChatContext from '../models/InternalChatContext';
import { ChatMessageAdded } from '../models/chatEvents';
import { ChatId } from '../models/ids';

export interface ChatOptions {
  type: ChatType;
  chatId?: ChatId;
  dossierId: DossierId;
  outletId?: OutletId;
  user: Identity;
  contactContext?: ContactContext;
}

class ChatService {
  private internalTargetCache = new Cache(ChatClient.loadInternalTargets);
  private clearUnreadMessagesDebounced = debounce(this.clearUnreadMessages, 15000);

  constructor() {
    serviceEvents.on(UserLoggedOut, () => this.internalTargetCache.clear());
  }

  async loadForDossier(dossier: Dossier, contactContext: ContactContext, type: ChatType, user: Identity): Promise<ChatContext> {
    const context = await this.doLoad({
      type,
      chatId: dossier.getChatId(type) ?? undefined,
      dossierId: dossier.id,
      outletId: dossier.outletId,
      user,
      contactContext,
    });
    context.internalMessageCount = dossier.internalChatCounter;
    context.externalMessageCount = dossier.externalChatCounter;
    context.eventBus.on(ChatMessageAdded, e => {
      dossier.setChatId(e.type, e.message.chatId);
      dossier.internalChatCounter = context.internalMessageCount ?? 0;
      dossier.externalChatCounter = context.externalMessageCount ?? 0;
    });
    return context;
  }

  loadForBooking(booking: Booking, type: ChatType, user: Identity): Promise<ChatContext> {
    const dossier = present(booking.dossier, 'cant load chat without dossier');
    return this.doLoad({
      type,
      chatId: booking.getChatId(type) ?? undefined,
      dossierId: dossier.id,
      outletId: booking.outlet.id,
      user,
    });
  }

  private async doLoad(options: ChatOptions): Promise<ChatContext> {
    // ensure the loading state is active during the entire process, especially also while preloading media thumbnails
    LoadingService.start();
    const contextPromise = this.createContext(options);
    const unbinder = this.startInternalPush(options.dossierId, contextPromise);
    const context = ref(await contextPromise) as Ref<ChatContext>;
    this.clearUnreadMessagesDebounced(context.value);
    await LoadingService.stop();

    context.value.freeHooks.push(
      unbinder,
      this.clearUnreadMessagesDebounced.cancel,
    );

    return context.value;
  }

  private async createContext(options: ChatOptions): Promise<ChatContext> {
    const chat = await ifPresent(options.chatId, ChatClient.load) ?? new Chat();
    chat.orderMessages();

    const contextPromise = visitChatType<Promise<ChatContext>>(options.type, {
      internal: async () => {
        const targets = await ifPresent(options.outletId, id => this.loadInternalTargets(id));
        return new InternalChatContext(chat, options.user, options.dossierId, targets);
      },
      external: async () => {
        const contacts = options.contactContext ?? await DossierContactService.loadAllForDossier(options.dossierId);
        return new ExternalChatContext(chat, contacts, options.user, options.dossierId);
      },
    });

    await chat.preloadMediaThumbnails();
    return contextPromise;
  }

  /**
   * Should be called even if internal chat is not active because it's used to update the message counters.
   * @param dossierId
   * @param contextPromise passing the context as a promise allows listening for push messages
   *          even before it has been fully created, which ensures we don't miss any messages.
   * @returns An unbinder function to unsubscribe from the push channel
   */
  private startInternalPush(dossierId: DossierId, contextPromise: Promise<ChatContext>): Unbinder {
    return pushService.bind(`dossiers-${dossierId}/new-internaldialog-message`, async data => {
      const message = present(parseChatMessage(data, data.chatId));
      const context = toRef(await contextPromise);
      context.value.addMessage(message, ChatType.INTERNAL);
    });
  }

  private async clearUnreadMessages(context: ChatContext): Promise<void> {
    const chat = context.chat;
    if (isPresent(chat.id) && chat.messages.length > 0 && chat.unreadMessages > 0 && context.canWrite()) {
      await ChatClient.markAsRead(chat.id);
      chat.unreadMessages = 0;
    }
  }

  async loadInternalTargets(outletId: OutletId): Promise<ChatTargetContainer> {
    const container = await this.internalTargetCache.get(outletId);
    container.order();
    return container;
  }

  async sendMessage(context: ChatContext): Promise<void> {
    if (!context.canSend()) return;
    const messages = await context.visitType({
      internal: () => this.sendInternalMessage(context as InternalChatContext),
      external: () => this.sendExternalMessage(context as ExternalChatContext),
    });
    context.afterSend(messages);
  }

  private async sendInternalMessage(context: InternalChatContext): Promise<Array<ChatMessage>> {
    return [await ChatMessageClient.sendInternal(context.dossierId, context.getSanitizedContent(), context.getMentionTargets(), context.job?.id ?? undefined)];
  }

  private async sendExternalMessage(context: ExternalChatContext): Promise<Array<ChatMessage>> {
    const chatId = present(context.chat.id);
    if (context.page === ChatPage.MEDIA) {
      return Promise.all(context.mediaFiles.map(mf => {
        return ChatMessageClient.sendExternal(chatId, present(mf.contentId), context.targets);
      }));
    } else {
      return [await ChatMessageClient.sendExternal(chatId, context.getSanitizedContent(), context.targets)];
    }
  }

  async sendExternalFiles(context: ExternalChatContext, files: Array<File>): Promise<void> {
    const chatId = present(context.chat.id);
    const messages = await Promise.all(files.map(f => ChatMessageClient.sendExternal(chatId, f, context.targets)));
    context.afterSend(messages);
  }
}

export default new ChatService();
